import { reactive, toRefs } from 'vue';
import { FieldInterface } from '@ui/components/forms/formFields.vue';
import { SmartFormFieldsInterface } from '@app/components/forms/smartFormFields.vue';
import { AlertsPanelInterface } from '@app/components/panels/alertsPanel.vue';
import { ButtonInterface } from '@ui/components/forms/button.vue';
import { AlertInterface } from '@/js/UI-SHARED/components/alerts/alert.vue';

export interface ISSO {
    tooltip: string;
    name: string;
    title: string;
    icon: string;
}
export interface ILoginButton extends ButtonInterface {
    name: string
    title: string
    type?: 'submit' | 'button'
    class?: string
}
export interface IUseLogin {
    alerts: AlertsPanelInterface
    form: SmartFormFieldsInterface
    sso: ISSO[]
    buttons?: ILoginButton[]
    workspace?: {
        name: string
    } | null
    onFormCallback?: ((callback: any) => void)[] | null;
    onSsoCallback?: ((callback: any) => void)[] | null;
    onButtonCallback?: ((callback: any) => void)[] | null;
}

const globalState: IUseLogin = reactive({
    alerts: null,
    form: {
        fields: [],
        loading: false,
    },
    workspace: null,
    buttons: [],
    sso: [],
    onFieldCallback: [],
    onSsoCallback: [],
    onButtonCallback: [],
});
export const useLogin = () => {
    const state = globalState;
    const onFormCallback = (callback: (field: FieldInterface | { action: 'submit' | 'validate' }) => void) => {
        if (!state.onFormCallback) {
            state.onFormCallback = [];
        }
        state.onFormCallback.push(callback);
    };
    const updateForm = (form: SmartFormFieldsInterface) => {
        state.form = { ...state.form, ...form };
    };

    const setLoadingIndicator = (value: boolean) => {
        state.form.loading = value;
    };

    const onFormField = (newField: FieldInterface) => {
        if (state.onFormCallback) {
            for (const callback of state.onFormCallback) {
                callback(newField);
            }
        }
    };

    const onSubmit = () => {
        if (state.onFormCallback) {
            for (const callback of state.onFormCallback) {
                callback({ action: 'submit' });
            }
        }
    };

    const updateSso = (sso: ISSO[]) => {
        state.sso = sso;
    };

    const onSsoCallback = (callback: (any) => void) => {
        if (!state.onSsoCallback) {
            state.onSsoCallback = [];
        }
        state.onSsoCallback.push(callback);
    };
    const onSSO = (sso: ISSO) => {
        if (state.onSsoCallback) {
            for (const callback of state.onSsoCallback) {
                callback(sso);
            }
        }
    };

    const addAlert = (alert: AlertInterface) => {
        if (state.alerts?.alerts) {
            state.alerts.alerts.push(alert);
        } else {
            state.alerts = { alerts: [alert] };
        }
    };

    const updateAlerts = (alerts: AlertsPanelInterface) => {
        state.alerts = alerts;
    };

    const updateButtons = (buttons: ILoginButton[]) => {
        state.buttons = buttons;
    };

    const onButtonCallback = (callback: (any) => void) => {
        if (!state.onButtonCallback) {
            state.onButtonCallback = [];
        }
        state.onButtonCallback.push(callback);
    };
    const onButton = (button: ILoginButton) => {
        if (state.onButtonCallback) {
            for (const callback of state.onButtonCallback) {
                callback(button);
            }
        }
    };
    const updateWorkspace = (workspace: { name: string }) => {
        state.workspace = workspace;
    };

    const removeFormFields = () => {
        state.form.fields = [];
    };

    const removeButtons = () => {
        state.buttons = null;
    };

    const removeAlerts = () => {
        state.alerts = null;
    };

    const removeSso = () => {
        state.sso = [];
    };
    const removeWorkspace = () => {
        state.workspace = null;
    };

    const validateForm = () => {
        if (state.onFormCallback) {
            for (const callback of state.onFormCallback) {
                callback({ action: 'validate' });
            }
        }
    };
    return {
        ...toRefs(state),
        updateForm,
        setLoadingIndicator,
        onFormCallback,
        onFormField,
        onSubmit,
        updateSso,
        onSsoCallback,
        onSSO,
        addAlert,
        updateAlerts,
        onButton,
        onButtonCallback,
        updateButtons,
        updateWorkspace,
        removeSso,
        removeAlerts,
        removeWorkspace,
        removeButtons,
        removeFormFields,
        validateForm,
    };
};
